import { Injectable, OnDestroy } from '@angular/core';
import {
  ActivatedRoute,
  ActivatedRouteSnapshot,
  NavigationEnd,
  Router,
  UrlSerializer,
} from '@angular/router';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { Logger } from 'src/app/shared/tcs-logging/logger';
import { takeUntil } from 'rxjs/operators';
import { getOrElse } from 'src/util/values';

export interface Breadcrumb {
  name: string;
  url?: string;
  extras?: any;
}

type LinkBehavior = 'match' | 'none';
interface LinkBehaviorConfig {
  link?: LinkBehavior;
}

type BreadcrumbRouteResolverConfig = {
  resolver: string;
} & ({ key?: string } | { nameSelector: (resolved: any) => string });

type BreadcrumbRouteConfig =
  | string
  | (({ name: string } | BreadcrumbRouteResolverConfig) & LinkBehaviorConfig);

export function breadcrumbs(...breadcrumbConfig: BreadcrumbRouteConfig[]): BreadcrumbRouteConfig[] {
  return breadcrumbConfig;
}

@Injectable({
  providedIn: 'root',
})
export class BreadcrumbService implements OnDestroy {
  private _onDestroy$ = new Subject();
  private _breadcrumbs$: BehaviorSubject<Breadcrumb[]> = new BehaviorSubject<Breadcrumb[]>([]);
  public get value$(): Observable<Breadcrumb[]> {
    return this._breadcrumbs$.asObservable();
  }
  constructor(
    private readonly _router: Router,
    private _activatedRoute: ActivatedRoute,
    private _urlSerializer: UrlSerializer,
    private _logger: Logger,
  ) {
    _router.events.pipe(takeUntil(this._onDestroy$)).subscribe((event) => {
      if (event instanceof NavigationEnd) {
        const crumbs: Breadcrumb[] = [];
        let route: ActivatedRouteSnapshot | null = this._activatedRoute.snapshot;
        while (route != null) {
          const segCrumbs: Breadcrumb[] = [];
          const segCfgsData: BreadcrumbRouteConfig[] | null | BreadcrumbRouteConfig =
            route.routeConfig != null && route.routeConfig.data != null
              ? route.routeConfig.data.breadcrumbs
              : null;

          if (segCfgsData != null) {
            const segConfigsArr: BreadcrumbRouteConfig[] =
              segCfgsData instanceof Array ? segCfgsData : [segCfgsData];
            const segCfgs = segConfigsArr.slice().reverse();
            for (let index = 0; index < segCfgs.length; index++) {
              const cfg = segCfgs[index];
              let name: string | undefined;
              let linkBehavior: LinkBehavior = 'match';
              if (typeof cfg === 'string') {
                name = cfg;
              } else if (typeof cfg === 'object' && 'resolver' in cfg) {
                const resolverData = route.data[cfg.resolver];
                if (resolverData != null) {
                  if ('key' in cfg && cfg.key != null) {
                    name = resolverData[cfg.key];
                  } else if ('nameSelector' in cfg && cfg.nameSelector != null) {
                    name = cfg.nameSelector(resolverData);
                  }
                  name = name != null ? name : resolverData.name;
                } else {
                  this._logger.warning(
                    `Breadcrumb was configured to get data from resolver named ${cfg.resolver}, but no resolved data was found.`,
                  );
                }
                linkBehavior = getOrElse(cfg.link, linkBehavior);
              } else {
                name = cfg.name;
                linkBehavior = getOrElse(cfg.link, linkBehavior);
              }
              if (name != null) {
                const segMatchIndex = route.url.length - index;
                if (segMatchIndex > 0 || route.url.length === 0) {
                  const segs = route.url.slice(0, segMatchIndex);
                  const segsFromRoot = route.pathFromRoot
                    .slice(0, route.pathFromRoot.length - 1)
                    .flatMap((r) => r.url.map((u) => u.path))
                    .concat(segs.map((s) => s.path));
                  const url = '/' + segsFromRoot.join('/');
                  const crumb = linkBehavior !== 'none' ? { name, url } : { name };
                  segCrumbs.push(crumb);
                }
              }
            }
            crumbs.push(...segCrumbs.slice().reverse());
          }
          route = route.firstChild;
        }
        this._breadcrumbs$.next(crumbs);
      }
    });
  }

  public ngOnDestroy(): void {
    this._onDestroy$.next(null);
    this._onDestroy$.complete();
  }
}
